// src/pages/SuperAdmin/Users/SARegisteredUsers.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Search, Trash2, Eye, Pencil } from "lucide-react";

import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserById,
} from "./userApi";

import ReusableModal from "../../../components/common/ReusableModal";
import FormButton from "../../../components/form/FormButton";
import FormInput from "../../../components/form/FormInput";
import Paginator from "../../../components/table/Paginator";
import UserForm from "./UserForm";

import "./users.css";

const SARegisteredUsers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchUsers = async (
    currentPage = page,
    currentPageSize = pageSize,
    currentSearch = search
  ) => {
    try {
      setLoading(true);

      const { data } = await listUsers({
        page: currentPage,
        limit: currentPageSize,
        search: currentSearch,
      });

      setItems(Array.isArray(data?.users) ? data.users : []);
      setTotal(Number(data?.pagination?.total || 0));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, pageSize, search);
  }, [page, pageSize, search]);

  const openEdit = async (u) => {
    try {
      setBusyId(u._id);
      const { data } = await getUserById(u._id);
      setEditing(data?.user || u);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load user");
      setEditing(u);
    } finally {
      setBusyId(null);
    }
  };

  const openView = async (u) => {
    try {
      setBusyId(u._id);
      const { data } = await getUserById(u._id);
      setViewing(data?.user || u);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load user details");
    } finally {
      setBusyId(null);
    }
  };

  const openConfirm = (u) => {
    setTargetUser(u);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (busyId === targetUser?._id) return;
    setConfirmOpen(false);
    setTargetUser(null);
  };

  const performDelete = async () => {
    if (!targetUser?._id) {
      closeConfirm();
      return;
    }

    try {
      setBusyId(targetUser._id);
      await deleteUser(targetUser._id);
      toast.success("User deleted");
      setConfirmOpen(false);
      setTargetUser(null);
      await fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async (payload) => {
    try {
      setBusyId("create");
      await createUser(payload);
      toast.success("User created");
      setCreateOpen(false);
      await fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Create failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleUpdate = async (payload) => {
    const id = editing?._id;
    if (!id) return;

    try {
      setBusyId(id);
      await updateUser(id, payload);
      toast.success("User updated");
      setEditing(null);
      await fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="sa-users-skin">
      <div className="sa-panel-hd">
        <h2>Registered Users</h2>

        <div className="sa-users-actions">
          <div className="sa-users-search">
            <div className="sa-users-search-box">
              <Search className="lucide sa-users-search-icon" />

              <FormInput
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search by name, email, or phone"
                className="sa-users-search-input"
              />
            </div>
          </div>

          <FormButton
            type="button"
            variant="primary"
            className="sa-users-create"
            onClick={() => setCreateOpen(true)}
          >
            + Create User
          </FormButton>
        </div>
      </div>

      <div className="sa-card sa-users-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading users…
          </div>
        ) : total === 0 ? (
          <div className="sa-empty">No users found.</div>
        ) : (
          <>
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right", width: 140 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((u, i) => (
                    <tr key={u._id}>
                      <td>{(page - 1) * pageSize + i + 1}</td>
                      <td>{u.fullName || "—"}</td>
                      <td>
                        {u.countryCode && u.mobileNumber
                          ? `${u.countryCode}${u.mobileNumber}`
                          : u.mobileNumber || "—"}
                      </td>
                      <td>{u.email || "—"}</td>
                      <td>
                        <span className={`sa-status ${u.isVerified ? "active" : "inactive"}`}>
                          {u.isVerified ? "Verified" : "Unverified"}
                        </span>
                      </td>

                      <td className="sa-actions-cell">
                        <div className="sa-actions">
                          <button
                            type="button"
                            className="sa-icon-link"
                            title="View"
                            aria-label="View user"
                            onClick={() => openView(u)}
                            disabled={busyId === u._id}
                          >
                            <Eye className="lucide" />
                          </button>

                          <button
                            type="button"
                            className="sa-icon-link"
                            title="Edit user"
                            aria-label="Edit user"
                            onClick={() => openEdit(u)}
                            disabled={busyId === u._id}
                          >
                            <Pencil className="lucide" />
                          </button>

                          <button
                            type="button"
                            className="sa-icon-link danger"
                            title="Delete user"
                            aria-label="Delete user"
                            onClick={() => openConfirm(u)}
                            disabled={busyId === u._id}
                          >
                            <Trash2 className="lucide" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sa-pager">
              <Paginator
                page={page}
                totalPages={totalPages}
                onPage={setPage}
                pageSize={pageSize}
                onPageSizeChange={(val) => {
                  setPage(1);
                  setPageSize(Number(val));
                }}
                pageSizeOptions={[5, 10, 20, 50]}
              />

              <div className="sa-gray">Total: {total}</div>
            </div>
          </>
        )}
      </div>

      <ReusableModal
        open={confirmOpen}
        title="Delete User"
        width="520px"
        className="users-delete-center-modal"
        onClose={closeConfirm}
      >
        <div className="users-delete-modal">
          <div className="sa-warn">
            This action will permanently delete this user. This cannot be undone.
          </div>

          <div className="sa-del-preview">
            <div className="sa-del-meta">
              <div className="sa-del-title">{targetUser?.fullName || "—"}</div>

              <div className="sa-del-sub">
                <span className="sa-gray">{targetUser?.email || "—"}</span>
                <span className="sa-dot">•</span>
                <span className="sa-gray">{targetUser?.mobileNumber || "—"}</span>
              </div>
            </div>
          </div>

          <div className="users-modal-actions">
            <FormButton
              type="button"
              variant="secondary"
              onClick={closeConfirm}
              disabled={busyId === targetUser?._id}
            >
              Cancel
            </FormButton>

            <FormButton
              type="button"
              variant="danger"
              onClick={performDelete}
              disabled={busyId === targetUser?._id}
            >
              {busyId === targetUser?._id ? "Deleting…" : "Delete"}
            </FormButton>
          </div>
        </div>
      </ReusableModal>

      <ReusableModal
        open={createOpen}
        title="Create User"
        width="640px"
        className="users-form-modal"
        closeOnBackdrop={false}
        onClose={() => {
          if (busyId !== "create") setCreateOpen(false);
        }}
      >
        <UserForm
          mode="create"
          submitting={busyId === "create"}
          onCancel={() => {
            if (busyId !== "create") setCreateOpen(false);
          }}
          onSubmit={handleCreate}
        />
      </ReusableModal>

      <ReusableModal
        open={!!editing}
        title="Edit User"
        width="640px"
        className="users-form-modal"
        closeOnBackdrop={false}
        onClose={() => {
          if (busyId !== editing?._id) setEditing(null);
        }}
      >
        <UserForm
          key={editing?._id}
          mode="edit"
          submitting={busyId === editing?._id}
          initialData={
            editing
              ? {
                  fullName: editing.fullName || "",
                  email: editing.email || "",
                  countryCode: editing.countryCode || "+91",
                  mobileNumber: editing.mobileNumber || "",
                  country: editing.country || "IN",
                }
              : undefined
          }
          onCancel={() => {
            if (busyId !== editing?._id) setEditing(null);
          }}
          onSubmit={handleUpdate}
        />
      </ReusableModal>

      <ReusableModal
        open={!!viewing}
        title="User Details"
        width="680px"
        onClose={() => setViewing(null)}
      >
        {viewing && (
          <div className="users-view-modal">
            <div className="users-view-head">
              <div>
                <div className="sa-h4 users-view-name">
                  {viewing.fullName || "—"}
                </div>

                <div className="sa-gray users-view-email">
                  {viewing.email || "—"}
                </div>
              </div>

              <span className={`sa-chip ${viewing.isVerified ? "sa-chip--success" : "sa-chip--danger"}`}>
                {viewing.isVerified ? "Verified" : "Unverified"}
              </span>
            </div>

            <div className="users-view-grid">
              <div className="users-view-card">
                <div className="users-kv">
                  <div>Full Name</div>
                  <div>{viewing.fullName || "—"}</div>
                </div>

                <div className="users-kv">
                  <div>Email</div>
                  <div>{viewing.email || "—"}</div>
                </div>

                <div className="users-kv">
                  <div>Mobile Number</div>
                  <div>{viewing.mobileNumber || "—"}</div>
                </div>
              </div>

              <div className="users-view-card">
                <div className="users-kv">
                  <div>Created At</div>
                  <div>{viewing.createdAt ? new Date(viewing.createdAt).toLocaleString() : "—"}</div>
                </div>

                <div className="users-kv">
                  <div>Updated At</div>
                  <div>{viewing.updatedAt ? new Date(viewing.updatedAt).toLocaleString() : "—"}</div>
                </div>
              </div>
            </div>

            <div className="users-modal-actions">
              <FormButton type="button" variant="primary" onClick={() => setViewing(null)}>
                Close
              </FormButton>
            </div>
          </div>
        )}
      </ReusableModal>
    </div>
  );
};

export default SARegisteredUsers;